import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CookieService } from '../services/cookies';
import { AppThemeContext } from './AppThemeContext';
import {
  applyAppTheme, DEFAULT_APP_THEME, detectBrowserAutoDarkTheme, type AppTheme, normalizeAppTheme,
  readSystemThemePreference, resolveAppTheme,
} from '../utils/appTheme';

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Browser and saved preferences are only knowable in the browser. Start with the
  // same theme as the static prerender, then synchronize after hydration.
  const [theme, setThemeState] = useState<AppTheme>(DEFAULT_APP_THEME);
  const preference = useRef<string | null>(null);

  const setTheme = useCallback((nextTheme: AppTheme) => {
    const normalizedTheme = normalizeAppTheme(nextTheme);
    preference.current = normalizedTheme;
    CookieService.setAppTheme(normalizedTheme);
    applyAppTheme(normalizedTheme);
    setThemeState(normalizedTheme);
  }, []);

  useEffect(() => {
    preference.current = CookieService.getAppTheme();
    const systemTheme = readSystemThemePreference();
    const syncTheme = () => {
      const nextTheme = resolveAppTheme(
        preference.current,
        systemTheme?.matches ?? false,
        detectBrowserAutoDarkTheme(),
      );
      applyAppTheme(nextTheme);
      setThemeState(nextTheme);
    };

    syncTheme();
    systemTheme?.addEventListener('change', syncTheme);
    return () => systemTheme?.removeEventListener('change', syncTheme);
  }, []);

  const contextValue = useMemo(() => ({
    theme,
    setTheme,
  }), [theme, setTheme]);

  return (
    <AppThemeContext.Provider value={contextValue}>
      {children}
    </AppThemeContext.Provider>
  );
}
